import React, { useEffect, useState, useCallback } from 'react';
import { isAuthenticated } from '../../services/auth';
import { message, Popconfirm, Result, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  Autocomplete,
  TextField,
  Box,
} from '@mui/material';
import {
  singleProduct,
  updateProduct,
  addProduct,
} from '../../services/product';
import { allLocations, allCategories, allCities, citiesByState } from '../../services/admin';
import { useHistory, Link } from 'react-router-dom';
import UnifiedDashboard from '../../components/layout/unified-dashboard';

const API = (process.env.REACT_APP_API ).replace(/\/api$/, '');

// Estados brasileiros
const BRAZILIAN_STATES = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];

const UpsertProduct = ({ match, isModal = false, onClose, returnUrl = '/user/dashboard', hideBackButton = false, productId }) => {
  const [values, setValues] = useState({
    name: '',
    category: '',
    state: '',
    city: '',
    neighborhood: null,
    description: '',
    condition: '',
    price: '',
    author: '',
    privateObservation: '',
    privatePhone: '',
    type: 'produto',
    propertyType: '',
    bedrooms: '',
    bathrooms: '',
    garages: '',
  });
  const [images, setImages] = useState([]);
  const [images2, setImages2] = useState([]);
  const [locations, setLocations] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const { user, token } = isAuthenticated();
  const { name, category, state, city, neighborhood, description, condition, price, author, privateObservation, privatePhone, type, propertyType, bedrooms, bathrooms, garages } = values;
  const history = useHistory();

  useEffect(() => {
    loadCategories();
    loadLocations();
    loadCities();
    
    // Verificar se é modo edição ou criação
    if (match?.params?.productId || (isModal && productId)) {
      setIsEditMode(true);
      loadProduct();
    } else {
      setIsEditMode(false);
      setLoading(false);
    }
  }, []);

  // Carregar cidades quando o estado muda
  useEffect(() => {
    if (state) {
      loadCitiesByState(state);
    } else {
      setCities([]);
    }
  }, [state]);

  const loadCategories = async () => {
    let res = await allCategories();
    setCategories(res.data);
  };

  const loadLocations = async () => {
    let res = await allLocations();
    setLocations(res.data);
  };

  const loadCities = async () => {
    let res = await allCities();
    setCities(res.data);
  };

  const loadCitiesByState = async (selectedState) => {
    try {
      let res = await citiesByState(selectedState);
      setCities(res.data);
    } catch (error) {
      console.error('Erro ao carregar cidades:', error);
      setCities([]);
    }
  };

  const loadProduct = async () => {
    setLoading(true);
    try {
      const prodId = isModal ? productId : match.params.productId;
      const res = await singleProduct(prodId, token);
      
      // Carregar dados necessários
      await loadLocations();
      if (res.data.state) {
        await loadCitiesByState(res.data.state);
      }
      
      const productData = {
        ...values,
        name: res.data.name,
        category: res.data.category._id,
        state: res.data.state || '',
        city: res.data.city?._id || '',
        neighborhood: res.data.neighborhood || null,
        description: res.data.description,
        condition: res.data.condition,
        price: res.data.price,
        author: res.data.author._id,
        privateObservation: res.data.privateObservation || '',
        privatePhone: res.data.privatePhone || '',
        type: res.data.type || 'produto',
        propertyType: res.data.propertyType || '',
        bedrooms: res.data.bedrooms || '',
        bathrooms: res.data.bathrooms || '',
        garages: res.data.garages || '',
      };
      setValues(productData);
      
      setImages(res.data.images);
      setImages2(res.data.images);
    } catch (err) {
      console.error(err);
      message.error('Erro ao carregar os dados do produto.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name) => (e) => {
    const value = e.target.value;
    setValues(prev => {
      const newValues = { ...prev, [name]: value };
      if (name === 'state') {
        newValues.city = '';
        newValues.neighborhood = null;
      }
      if (name === 'city') {
        newValues.neighborhood = null;
      }
      return newValues;
    });
  };

  const handleImageChange = (e) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      const newPreviews = filesArray.map((file) => URL.createObjectURL(file));

      setImages((prevImages) => [...prevImages, ...filesArray]);
      setImages2((prevPreviews) => [...prevPreviews, ...newPreviews]);
    }
  };

  const handleRemoveImage = (index) => {
    setDeleteIndex(index);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteImage = () => {
    setImages((prevImages) => prevImages.filter((_, i) => i !== deleteIndex));
    setImages2((prevPreviews) => {
      const previewToRemove = prevPreviews[deleteIndex];

      if (typeof previewToRemove === 'string' && previewToRemove.startsWith('blob:')) {
        URL.revokeObjectURL(previewToRemove);
      }

      return prevPreviews.filter((_, i) => i !== deleteIndex);
    });
    setShowDeleteConfirm(false);
    setDeleteIndex(null);
  };

  const cancelDeleteImage = () => {
    setShowDeleteConfirm(false);
    setDeleteIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !category || !state || !city || !description || !price) {
      message.error('Preencha todos os campos obrigatórios', 4);
      return;
    }

    if (!isEditMode && images.length === 0) {
      message.error('Selecione pelo menos uma imagem', 4);
      return;
    }

    setLoading(true);

    let productData = new FormData();
    productData.append('name', name);
    productData.append('category', category);
    productData.append('state', state);
    productData.append('city', city);
    productData.append('neighborhood', neighborhood ? neighborhood._id : '');
    productData.append('description', description);
    productData.append('condition', condition);
    productData.append('price', price);
    productData.append('privateObservation', privateObservation);
    productData.append('privatePhone', privatePhone);
    productData.append('type', type);
    
    // Apenas adiciona campos de imóvel se for tipo 'produto'
    if (type === 'produto') {
      productData.append('propertyType', propertyType);
      productData.append('bedrooms', bedrooms);
      productData.append('bathrooms', bathrooms);
      productData.append('garages', garages);
    }

    try {
      if (isEditMode) {
        // Edição
        productData.append('author', author);
        productData.append('userAuth', user._id);

        images2.forEach((image) => {
          if (typeof image === 'string' && !image.startsWith('blob:')) {
            productData.append('existingImages', image);
          }
        });

        images.forEach((image) => {
          if (image instanceof File) {
            productData.append('images', image);
          }
        });

        await updateProduct(isModal ? productId : match.params.productId, productData, token);
        message.success('Produto atualizado com sucesso', 4);
      } else {
        // Criação
        images.forEach((image) => {
          if (image instanceof File) {
            productData.append('images', image);
          }
        });

        await addProduct(match.params.userId, productData, token);
        message.success('Produto adicionado com sucesso', 4);
      }

      if (isModal && onClose) {
        onClose();
      } else {
        history.push('/user/dashboard');
      }
    } catch (err) {
      console.log(err);
      if (err.response && err.response.status === 400) {
        message.error(err.response.data?.error || err.response.data || 'Erro ao salvar produto.', 4);
      } else {
        message.error('Erro ao salvar produto.', 4);
      }
    } finally {
      setLoading(false);
    }
  };

  const upsertProductForm = () => (
    <form onSubmit={handleSubmit} noValidate className='upsert-product-form'>
      <div className='form-group'>
        <label className='form-label'>Nome do Produto*</label>
        <input
          type='text'
          className='form-control'
          placeholder='Digite o nome do produto'
          value={name}
          onChange={handleChange('name')}
          disabled={loading}
        />
      </div>

      <div className='form-group'>
        <h6>Imagens*</h6>
        <div className='upsert-product-images-section'>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-start' }}>
            <label className='upsert-product-plus-btn' style={{ cursor: loading ? 'not-allowed' : 'pointer' }}>
              <i className='fas fa-plus'></i>
              <input
                onChange={handleImageChange}
                type='file'
                name='images'
                accept='image/*'
                multiple
                hidden
                disabled={loading}
              />
            </label>
            {images2 && images2.length > 0 && images2.map((image, i) => {
              const isBlob = typeof image === 'string' && image.startsWith('blob:');
              const imageUrl = isBlob ? image : `${API}${image.replace(/^[\.\/]playnoimovel\.com\.br\/api/, '')}`;
              return (
                <div className='upsert-product-preview-container' key={i}>
                  <img
                    src={imageUrl}
                    alt='preview_image'
                    className='upsert-product-preview-img'
                  />
                  <i
                    className='fas fa-times-circle upsert-product-remove-icon'
                    role='button'
                    onClick={() => handleRemoveImage(i)}
                  ></i>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className='form-group'>
        <h6>Tipo de Anúncio*</h6>
        <div style={{ display: 'flex', gap: '2rem', marginTop: '0.75rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>
            <input
              type='radio'
              name='type'
              value='produto'
              checked={type === 'produto'}
              onChange={handleChange('type')}
              disabled={loading}
              style={{ marginRight: '0.5rem', cursor: 'pointer' }}
            />
            <span>Imóvel</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>
            <input
              type='radio'
              name='type'
              value='servico'
              checked={type === 'servico'}
              onChange={handleChange('type')}
              disabled={loading}
              style={{ marginRight: '0.5rem', cursor: 'pointer' }}
            />
            <span>Serviço</span>
          </label>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.75rem' }}>
        <div className='form-group'>
          <h6>Categoria*</h6>
          <select
            className='form-select'
            onChange={handleChange('category')}
            value={category}
            disabled={loading}
          >
            <option value=''>Selecione a categoria</option>
            {categories
              .filter(c => (c.type || 'produto') === type)
              .map((c, i) => (
                <option key={i} value={c._id}>
                  {c.name}
                </option>
              ))}
          </select>
        </div>
        <div className='form-group'>
          <h6>Estado*</h6>
          <select
            className='form-select'
            onChange={handleChange('state')}
            value={state}
            disabled={loading}
          >
            <option value=''>Selecione o estado</option>
            {BRAZILIAN_STATES.map((st, i) => (
              <option key={i} value={st.value}>
                {st.label}
              </option>
            ))}
          </select>
        </div>
      </div>



      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.75rem' }}>
        <div className='form-group'>
          <h6>Cidade*</h6>
          <Autocomplete
            options={cities}
            getOptionLabel={(option) => option.name || ''}
            value={cities.find(c => c._id === city) || null}
            onChange={(event, newValue) => {
              setValues(prev => ({ ...prev, city: newValue?._id || '', neighborhood: '' }));
            }}
            disabled={loading || !state}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Selecione ou busque uma cidade"
                variant="outlined"
                size="small"
                fullWidth
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option.name}
              </Box>
            )}
            noOptionsText="Nenhuma cidade encontrada"
            loading={loading}
            loadingText="Carregando cidades..."
          />
        </div>
        <div className='form-group'>
          <h6>Bairro (opcional)</h6>
          <Autocomplete
            options={locations.filter(l => !city || l.city._id === city || l._id === neighborhood?._id)}
            getOptionLabel={(option) => option.name || ''}
            value={neighborhood}
            onChange={(event, newValue) => {
              setValues(prev => ({ ...prev, neighborhood: newValue }));
            }}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Selecione ou busque um bairro"
                variant="outlined"
                size="small"
                fullWidth
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option.name} - {option.city?.name || ''}, {option.city?.state || ''}
              </Box>
            )}
            noOptionsText="Nenhum bairro encontrado"
            loading={loading}
            loadingText="Carregando bairros..."
          />
        </div>
      </div>

      <div className='form-group'>
        <label className='form-label'>Descrição*</label>
        <textarea
          className='form-control upsert-product-description'
          placeholder='Digite uma descrição detalhada do produto'
          value={description}
          onChange={handleChange('description')}
          disabled={loading}
        />
      </div>

      {type === 'produto' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.75rem' }}>
          <div className='form-group'>
            <label className='form-label'>Tipo de Imóvel</label>
            <select
              className='form-select'
              onChange={handleChange('propertyType')}
              value={propertyType}
              disabled={loading}
            >
              <option value=''>Selecione o tipo</option>
              <option value='apartamento'>Apartamento</option>
              <option value='casa'>Casa</option>
              <option value='condominio'>Condomínio</option>
              <option value='terreno'>Terreno</option>
              <option value='comercial'>Comercial</option>
              <option value='outro'>Outro</option>
            </select>
          </div>

          <div className='form-group'>
            <label className='form-label'>Dormitórios</label>
            <select
              className='form-select'
              onChange={handleChange('bedrooms')}
              value={bedrooms}
              disabled={loading}
            >
              <option value=''>Selecione</option>
              {[1, 2, 3, 4, 5, 6].map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>

          <div className='form-group'>
            <label className='form-label'>Banheiros</label>
            <select
              className='form-select'
              onChange={handleChange('bathrooms')}
              value={bathrooms}
              disabled={loading}
            >
              <option value=''>Selecione</option>
              {[1, 2, 3, 4, 5].map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>

          <div className='form-group'>
            <label className='form-label'>Garagens</label>
            <select
              className='form-select'
              onChange={handleChange('garages')}
              value={garages}
              disabled={loading}
            >
              <option value=''>Selecione</option>
              {[1, 2, 3, 4, 5].map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.75rem' }}>
        <div className='form-group'>
          <h6>Condição</h6>
          <select
            className='form-select'
            onChange={handleChange('condition')}
            value={condition}
            disabled={loading}
          >
            <option value=''>Selecione a condição</option>
            <option value='not_applicable'>Não se aplica</option>
            <option value='new'>Novo</option>
            <option value='used'>Usado</option>
          </select>
        </div>
        <div className='form-group'>
          <label className='form-label'>Preço*</label>
          <div className='input-group'>
            <span className='input-group-text'>R$</span>
            <input
              type='number'
              step='0.01'
              className='form-control'
              placeholder='0,00'
              value={price}
              onChange={handleChange('price')}
              disabled={loading}
            />
          </div>
        </div>
      </div>

      <div className='form-group'>
        <label className='form-label'>Observação Privada</label>
        <textarea
          className='form-control upsert-product-private-observation'
          placeholder='Digite uma observação privada (apenas visível para você)'
          value={privateObservation}
          onChange={handleChange('privateObservation')}
          disabled={loading}
        />
      </div>

      <div className='form-group'>
        <label className='form-label'>Telefone Privado</label>
        <input
          type='text'
          className='form-control'
          placeholder='(00) 00000-0000'
          value={privatePhone}
          onChange={handleChange('privatePhone')}
          disabled={loading}
        />
      </div>

      <div className='upsert-product-actions'>
        <button
          type='submit'
          className='upsert-product-submit-btn'
          disabled={loading}
        >
          {loading ? (
            <>
              <Spin size='small' style={{ marginRight: '8px' }} />
              Processando...
            </>
          ) : (
            isEditMode ? 'Atualizar Produto' : 'Anunciar Produto'
          )}
        </button>
      </div>
    </form>
  );

  if (loading && isEditMode) {
    return (
      <div className='upsert-product-loading'>
        <Spin size='large' />
      </div>
    );
  }

  if (isEditMode && author && author !== user._id && user.role !== 'admin') {
    return (
      <UnifiedDashboard>
        <Result
          status='403'
          title='403'
          subTitle='Desculpe, você não está autorizado a acessar esta página.'
          extra={
            <Link to='/' className='upsert-product-submit-btn' style={{ display: 'inline-block', textDecoration: 'none' }}>
              Voltar para Home
            </Link>
          }
        />
      </UnifiedDashboard>
    );
  }

  return (
    <>
      {isModal ? (
        upsertProductForm()
      ) : (
        <UnifiedDashboard>
          <div className='upsert-product-container'>
            <div className='upsert-product-header-section'>
              {!hideBackButton && (
                <button 
                  className='upsert-product-back-btn-header'
                  onClick={() => history.goBack()}
                  title="Voltar"
                >
                  <ArrowLeftOutlined />
                </button>
              )}
              <h2 className='upsert-product-title'>
                <i className='fas fa-tags'></i>
                {isEditMode ? 'Editar Produto' : 'Anunciar Produto'}
              </h2>
              <p className='upsert-product-subtitle'>{isEditMode ? 'Atualize as informações do seu produto' : 'Crie um novo anúncio'}</p>
            </div>
            <div className='upsert-product-form-wrapper'>
              {upsertProductForm()}
            </div>
          </div>
        </UnifiedDashboard>
      )}
      {/* Delete Image Confirm Modal */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>Excluir imagem</h3>
            <p style={{ margin: '0 0 20px 0', color: '#666' }}>Tem certeza que deseja excluir esta imagem?</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={cancelDeleteImage}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d9d9d9',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Não
              </button>
              <button
                onClick={confirmDeleteImage}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  backgroundColor: '#ff4d4f',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Sim
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UpsertProduct;
